
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";

interface UseFilterDataProps {
  selectedRegion: string;
  selectedCountry: string;
  selectedAccount: string;
  selectedProduct: string;
  selectedIndustry: string;
  filterType: string;
  setSelectedCountry: (value: string) => void;
  setSelectedAccount: (value: string) => void;
  setSelectedProduct: (value: string) => void;
  setSelectedIndustry: (value: string) => void;
}

export const useFilterData = (props: UseFilterDataProps) => {
  const [regions, setRegions] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [accounts, setAccounts] = useState<string[]>([]);
  const [products, setProducts] = useState<string[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);

  const fetchCountriesByRegion = async () => {
    try {
      let query = supabase.from('leaderboard').select('country').order('country');
      if (props.selectedRegion !== "all") {
        query = query.eq('region', props.selectedRegion);
      }
      const { data, error } = await query;
      if (error) {
        console.error('Error fetching country data by region:', error);
        return;
      }
      if (data) {
        const uniqueCountries = Array.from(new Set(data.map(item => item.country))).filter(country => country !== null && country !== undefined && country !== "") as string[];
        setCountries(uniqueCountries);

        if (props.selectedCountry !== "all" && !uniqueCountries.includes(props.selectedCountry)) {
          props.setSelectedCountry("all");
        }
      }
    } catch (error) {
      console.error('Error in fetchCountriesByRegion:', error);
    }
  };

  const fetchAccountsByRegionAndCountry = async () => {
    try {
      let query = supabase.from('leaderboard').select('account_name').order('account_name');
      if (props.selectedRegion !== "all") {
        query = query.eq('region', props.selectedRegion);
      }
      if (props.selectedCountry !== "all") {
        query = query.eq('country', props.selectedCountry);
      }
      const { data, error } = await query;
      if (error) {
        console.error('Error fetching account data by region and country:', error);
        return;
      }
      if (data) {
        const uniqueAccounts = Array.from(new Set(data.map(item => item.account_name))).filter(account => account !== null && account !== undefined && account !== "") as string[];
        setAccounts(uniqueAccounts);

        if (props.selectedAccount !== "all" && !uniqueAccounts.includes(props.selectedAccount)) {
          props.setSelectedAccount("all");
        }
      }
    } catch (error) {
      console.error('Error in fetchAccountsByRegionAndCountry:', error);
    }
  };

  const fetchProductsByFilters = async () => {
    try {
      let query = supabase.from('leaderboard').select('product_line').order('product_line');
      if (props.selectedRegion !== "all") {
        query = query.eq('region', props.selectedRegion);
      }
      if (props.selectedCountry !== "all") {
        query = query.eq('country', props.selectedCountry);
      }
      if (props.selectedAccount !== "all") {
        query = query.eq('account_name', props.selectedAccount);
      }
      const { data, error } = await query;
      if (error) {
        console.error('Error fetching product data by filters:', error);
        return;
      }
      if (data) {
        const uniqueProducts = Array.from(new Set(data.map(item => item.product_line))).filter(product => product !== null && product !== undefined && product !== "") as string[];
        setProducts(uniqueProducts);

        if (props.selectedProduct !== "all" && !uniqueProducts.includes(props.selectedProduct)) {
          props.setSelectedProduct("all");
        }
      }
    } catch (error) {
      console.error('Error in fetchProductsByFilters:', error);
    }
  };

  const fetchIndustriesByFilters = async () => {
    try {
      let query = supabase.from('leaderboard').select('industry').order('industry');
      if (props.selectedRegion !== "all") {
        query = query.eq('region', props.selectedRegion);
      }
      if (props.selectedCountry !== "all") {
        query = query.eq('country', props.selectedCountry);
      }
      if (props.selectedAccount !== "all") {
        query = query.eq('account_name', props.selectedAccount);
      }
      const { data, error } = await query;
      if (error) {
        console.error('Error fetching industry data by filters:', error);
        return;
      }
      if (data) {
        const uniqueIndustries = Array.from(new Set(data.map(item => item.industry))).filter(industry => industry !== null && industry !== undefined && industry !== "") as string[];
        setIndustries(uniqueIndustries);

        if (props.selectedIndustry !== "all" && !uniqueIndustries.includes(props.selectedIndustry)) {
          props.setSelectedIndustry("all");
        }
      }
    } catch (error) {
      console.error('Error in fetchIndustriesByFilters:', error);
    }
  };

  const fetchFilterData = async () => {
    try {
      const { data: regionData } = await supabase.from('leaderboard').select('region').order('region');
      if (regionData) {
        const uniqueRegions = Array.from(new Set(regionData.map(item => item.region)));
        setRegions(uniqueRegions);
      }

      await fetchCountriesByRegion();
      await fetchAccountsByRegionAndCountry();
      if (props.filterType === "product") {
        await fetchProductsByFilters();
      } else {
        await fetchIndustriesByFilters();
      }
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  return {
    regions,
    countries,
    accounts,
    products,
    industries,
    fetchCountriesByRegion,
    fetchAccountsByRegionAndCountry,
    fetchProductsByFilters,
    fetchIndustriesByFilters,
    fetchFilterData
  };
};
